import { useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames/bind'
import { isEmpty, find } from 'lodash-es'

// Libs
import { types as selectTypes } from '../../reducer/selectReducer'

// Components
import { AutoSizer, List, CellMeasurerCache, CellMeasurer } from 'react-virtualized'
import Option from '../Option'

// Styles
import styles from './style.module.scss'

// Variables
const cx = classnames.bind(styles)

// PropTypes
export const propTypes = {
  options: PropTypes.array,
  selectedOption: PropTypes.array,
  setIsMenuOpened: PropTypes.func,
  controlRef: PropTypes.object,
  inputRef: PropTypes.object,
  fieldItemRef: PropTypes.object,
  dispatch: PropTypes.func,
  handleOptionChange: PropTypes.func,
  isMulti: PropTypes.bool,
  maxLength: PropTypes.number,
}

// DefaultProps
export const defaultProps = {}

const cache = new CellMeasurerCache({
  defaultHeight: 48,
  minHeight: 48,
  fixedWidth: true,
})

function MenuList(props) {
  const { options, selectedOption, controlRef, inputRef, fieldItemRef, dispatch, handleOptionChange, isMulti, maxLength } = props

  const menuListRef = useRef(null)

  const hasOptions = options.length > 0

  const handleOptionSelect = (option) => {
    if (isMulti) {
      handleOptionChange(selectedOption.concat(option))

      inputRef.current?.focus()

      if (selectedOption.length >= maxLength) return

      dispatch({
        type: selectTypes.HANDLE_OPTION_MULTI_SELECT,
        selectedOption: option,
      })
    } else {
      handleOptionChange(option)

      dispatch({
        type: selectTypes.HANDLE_OPTION_SINGLE_SELECT,
        selectedOption: option,
      })
    }
  }

  const rowRenderer = ({ index, key, parent, style }) => {
    const optionData = options[index]

    const isSelected = !isEmpty(find(selectedOption, { value: optionData?.value }))

    return (
      <CellMeasurer cache={cache} columnIndex={0} key={key} parent={parent} rowIndex={index}>
        {({ registerChild }) => (
          <div key={key} ref={registerChild} style={style} onClick={() => handleOptionSelect(optionData)}>
            <Option {...optionData} isSelected={isSelected} />
          </div>
        )}
      </CellMeasurer>
    )
  }

  const rowHeight = 48
  const maxListHeight = 220
  const rowLength = options.length ?? 1
  const listHeight = rowLength * rowHeight >= maxListHeight ? maxListHeight : rowLength * rowHeight

  useEffect(() => {
    const onBodyClick = (event) => {
      event.preventDefault()

      const isControlClick = controlRef.current?.contains(event.target)
      const isMenuListClick = menuListRef.current?.contains(event.target)
      const isFieldItemClick = fieldItemRef.current?.contains(event.target)

      if (!isMenuListClick && !isControlClick && !isFieldItemClick) {
        dispatch({
          type: selectTypes.HANDLE_INPUT_CLEAR,
        })
      }
    }

    window.document.addEventListener('click', onBodyClick)

    return () => {
      window.document.removeEventListener('click', onBodyClick)
    }
  }, [controlRef, dispatch])

  return (
    <div className={cx('menu')} ref={menuListRef} style={{ height: listHeight }}>
      {hasOptions && (
        <AutoSizer>
          {({ width, height }) => {
            return (
              <List
                className={cx('menu-list')}
                deferredMeasurementCache={cache}
                rowHeight={cache.rowHeight}
                width={width}
                height={height}
                rowCount={rowLength}
                rowRenderer={rowRenderer}
              />
            )
          }}
        </AutoSizer>
      )}

      {!hasOptions && <div className={cx('menu-empty')}>No options</div>}
    </div>
  )
}

MenuList.propTypes = propTypes
MenuList.defaultProps = defaultProps

export default MenuList
